<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
  >
    <template v-slot:header>
      <v-card-title class="screen-title">
        インターネットセキュリティアプライアンスポリシー設定
      </v-card-title>
      <button type="button" aria-label="Close" class="close" @click="submit">
        ×
      </button>
    </template>
    <template v-slot:body>
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid class="pa-0">
            <div class="font-weight-bold mt-3">
              ゾーン一覧画面で、反映ボタンを押さないと機器には反映されません
            </div>
            {{ zoneRelationToDisplay }}
            <v-sheet>
              <v-tabs hide-slider grow v-model="tabValue">
                <v-tab
                  v-if="contractSummary.isNat"
                  :text="
                    isInternetToAccess
                      ? 'NAT(IP Forwarding)'
                      : 'NAT(IP Masquerade)'
                  "
                  value="NAT"
                />
                <v-tab text="UTM" value="UTM" />
              </v-tabs>
              <v-tabs-window
                v-model="tabValue"
                class="border-s-sm border-b-sm border-e-sm"
              >
                <v-tabs-window-item v-if="contractSummary.isNat" value="NAT">
                  <template v-if="isInternetToAccess">
                    <v-row
                      no-gutters
                      justify="space-between"
                      class="my-2"
                      align="center"
                    >
                      <span>{{ natPolicyList.policyList.length }}件を表示</span>
                      <v-btn
                        color="primary"
                        @click="editPolicyDestinationNAT"
                        v-permission.config
                      >
                        ポリシー編集
                      </v-btn>
                    </v-row>
                    <app-table
                      density="compact"
                      :fields="destinationNatTable.fields"
                      :items="natPolicyList.policyList"
                      :per-page="-1"
                      primary-key="policyId"
                      :td-class="tdClass"
                      :tr-class="() => undefined"
                    >
                      <!-- 送信元 -->
                      <template #cell(srcAddressList)="{ value }">
                        <policy-src-address
                          :src-address="{ srcAddressList: value }"
                        />
                      </template>

                      <!-- IPマッピング -->
                      <template #cell(ipMapping)="{ value }">
                        {{ value.ipMappingName }}
                      </template>

                      <!-- 有効/有無 -->
                      <template #cell(isPolicyStatus)="{ value }">
                        {{ $filter.enumTo(value, "enable") }}
                      </template>

                      <!-- 備考 -->
                      <template #cell(description)="{ value }">
                        {{ $filter.emptyTo(value, "-") }}
                      </template>
                    </app-table>
                  </template>
                  <template v-else>
                    <v-row
                      no-gutters
                      justify="space-between"
                      class="my-2"
                      align="center"
                    >
                      <span
                        >{{
                          ipMasqueradePolicyList.policyList.length
                        }}件を表示</span
                      >
                      <v-btn
                        color="primary"
                        v-permission.config
                        @click="editPolicyIpMasquerade"
                      >
                        ポリシー編集
                      </v-btn>
                    </v-row>
                    <app-table
                      density="compact"
                      :fields="ipMasqueradeTable.fields"
                      :items="ipMasqueradePolicyList.policyList"
                      :per-page="-1"
                      primary-key="policyId"
                      :td-class="tdClass"
                      :tr-class="policyRowClass"
                    >
                      <!-- 送信元 -->
                      <template #cell(srcAddressList)="{ value }">
                        <policy-src-address
                          :src-address="{ srcAddressList: value }"
                        />
                      </template>

                      <!-- 宛先 -->
                      <template #cell(dstAddressList)="{ value }">
                        <policy-dst-address
                          :dst-address="{ dstAddressList: value }"
                        />
                      </template>

                      <!-- IP Pool -->
                      <template #cell(ipPoolList)="{ value }">
                        <policy-ip-pool :ip-pool-list="value" />
                      </template>

                      <!-- 有効/有無 -->
                      <template #cell(isPolicyStatus)="{ value }">
                        {{ $filter.enumTo(value, "enable") }}
                      </template>

                      <!-- 備考 -->
                      <template #cell(description)="{ value }">
                        {{ $filter.emptyTo(value, "-") }}
                      </template>
                    </app-table>
                  </template>
                </v-tabs-window-item>
                <v-tabs-window-item value="UTM">
                  <v-row
                    no-gutters
                    justify="space-between"
                    class="my-2"
                    align="center"
                  >
                    <span>{{ utmPolicyList.policyList.length }}件表示</span>
                    <v-btn
                      color="primary"
                      v-permission.config
                      @click="editPolicyUTM"
                      >ポリシー編集
                    </v-btn>
                  </v-row>
                  <app-table
                    density="compact"
                    :fields="utmTable.fields"
                    :items="utmPolicyList.policyList"
                    :per-page="-1"
                    primary-key="policyId"
                    :td-class="tdClass"
                    :tr-class="policyRowClass"
                  >
                    <!-- 送信元 -->
                    <template #cell(srcAddress)="{ value }">
                      <policy-src-address :src-address="value" />
                    </template>

                    <!-- 宛先 -->
                    <template #cell(dstAddress)="{ value }">
                      <policy-dst-address :dst-address="value" />
                    </template>

                    <!-- サービス -->
                    <template #cell(service)="{ item }">
                      <policy-service
                        :service-option="item.serviceOption"
                        :default-service-list="item.defaultServiceList"
                        :custom-service-list="item.customServiceList"
                      />
                    </template>

                    <!-- プロファイル -->
                    <template #cell(profile)="{ value }">
                      <policy-profile :profile="value" />
                    </template>

                    <!-- アクション -->
                    <template #cell(actionType)="{ value }">
                      {{ $filter.enumTo(value, "allow_deny") }}
                    </template>

                    <!-- ログ出力 -->
                    <template #cell(isLogOutput)="{ value }">
                      {{ $filter.enumTo(value, "on_off") }}
                    </template>

                    <!-- 有効/有無 -->
                    <template #cell(isPolicyStatus)="{ value }">
                      {{ $filter.enumTo(value, "enable") }}
                    </template>

                    <!-- 備考 -->
                    <template #cell(description)="{ value }">
                      {{ $filter.emptyTo(value, "-") }}
                    </template>
                  </app-table>
                </v-tabs-window-item>
              </v-tabs-window>
            </v-sheet>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn variant="outlined" color="primary" @click="submit">
              戻る
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { GetPolicy, GetPolicyType1 } from "@/apis/NwPolicyApi";
import type { ZoneRelationItem } from "@/apis/ZoneRelationApi";
import cloneDeep from "lodash/cloneDeep";
import type { Type1Service } from "@/apis/ContractChangeType1Api";
import { mapState } from "vuex";
import { VueFinalModal } from "vue-final-modal";
import PolicySrcAddress from "@/components/policy/PolicySrcAddress.vue";
import PolicyDstAddress from "@/components/policy/PolicyDstAddress.vue";
import PolicyIpPool from "@/components/policy/PolicyIpPool.vue";
import PolicyProfile from "@/components/policy/PolicyProfile.vue";
import PolicyService from "@/components/policy/PolicyService.vue";
import type { AppTableHeader } from "@/components/AppTable.vue";
import InternetType1PolicyRuleUtmViewRD from "@/modals/applianceContractSetting/type1/policy/utm/InternetType1PolicyRuleUtmViewRD.vue";
import InternetType1PolicyRuleIpMasqViewRD from "@/modals/applianceContractSetting/type1/policy/ipMasquerade/InternetType1PolicyRuleIpMasqViewRD.vue";
import InternetType1PolicyRuleDestNatViewRD from "@/modals/applianceContractSetting/type1/policy/ipForwarding/InternetType1PolicyRuleDestNatViewRD.vue";

export type Type1UTMPolicyList =
  | NonNullable<
      GetPolicyType1["policyUtmType1"]
    >["internetToAccessPointList"][0]
  | NonNullable<
      GetPolicyType1["policyUtmType1"]
    >["accessPointToInternetList"][0];
export type Type1UTMPolicy = Type1UTMPolicyList["policyList"][0];

export type Type1IpMasqueradeList = NonNullable<
  GetPolicyType1["policyIPMasqueradeType1List"]
>[0];

export type Type1NatPolicyList = NonNullable<
  GetPolicyType1["policyNatType1List"]
>[0];

export default defineComponent({
  name: "InternetType1PolicyUtmList",
  components: {
    PolicyIpPool,
    PolicyProfile,
    PolicyService,
    PolicyDstAddress,
    PolicySrcAddress,
  },
  props: {
    /** zone relation */
    zoneRelationItem: {
      type: Object as PropType<ZoneRelationItem>,
      required: true,
    },
    /** type1 policy */
    policy: {
      type: Object as PropType<GetPolicyType1>,
      required: true,
    },
    /** サービスメニュー(インターネットType1) */
    type1Service: {
      type: Object as PropType<Type1Service>,
      required: true,
    },
  },
  data() {
    const clonePolicy: GetPolicyType1 = cloneDeep(this.policy);
    // NATはデフォルトポリシーがないため初期はゾーンが一致するオブジェクトが無い場合がある
    // その際はポリシー0件のオブジェクトを作成（NAT契約がある場合のみ）
    if (
      this.$store.state.user.contractSummary.isNat &&
      !clonePolicy.policyNatType1List!.find(
        (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
      )
    ) {
      clonePolicy.policyNatType1List! = [
        ...clonePolicy.policyNatType1List!,
        {
          zoneRelationId: this.zoneRelationItem.zoneRelationId,
          total: 0,
          policyList: [],
        },
      ];
    }

    return {
      tabValue: "UTM" as "NAT" | "UTM",
      utmTable: {
        fields: [
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddress", title: "送信元アドレス", width: "150px" },
          { key: "dstAddress", title: "宛先アドレス", width: "150px" },
          { key: "service", title: "サービス", width: "15%" },
          { key: "profile", title: "プロファイル", width: "20%" },
          { key: "actionType", title: "アクション", width: "80px" },
          { key: "isLogOutput", title: "ログ出力", width: "70px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
        ],
      },
      ipMasqueradeTable: {
        fields: [
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddressList", title: "送信元アドレス", width: "150px" },
          { key: "dstAddressList", title: "宛先アドレス", width: "150px" },
          { key: "ipPoolList", title: "IP Pool", width: "150px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
        ],
      },
      destinationNatTable: {
        fields: [
          { key: "policyId", title: "ID", width: "80px" },
          { key: "srcAddressList", title: "送信元アドレス", width: "150px" },
          { key: "ipMapping", title: "IPマッピング", width: "200px" },
          { key: "isPolicyStatus", title: "有効/無効", width: "70px" },
          { key: "description", title: "備考" },
        ],
      },
      clonePolicy,
    };
  },
  computed: {
    ...mapState("user", {
      contractSummary: "contractSummary",
    }),
    /**
     * zone relationの表示
     */
    zoneRelationToDisplay(): string {
      return (
        this.$filter.enumTo(this.zoneRelationItem.srcZoneType, "zoneType") +
        "/" +
        this.zoneRelationItem.srcZoneName +
        " → " +
        this.$filter.enumTo(this.zoneRelationItem.dstZoneType, "zoneType") +
        "/" +
        this.zoneRelationItem.dstZoneName
      );
    },
    isInternetToAccess(): boolean {
      return this.zoneRelationItem.srcZoneType === "INTERNET";
    },
    /** 方向 + ゾーンが一致するポリシーリスト */
    utmPolicyList(): Type1UTMPolicyList {
      if (this.isInternetToAccess) {
        // UTMインターネット発
        return this.clonePolicy.policyUtmType1!.internetToAccessPointList.find(
          (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
        )!;
      } else {
        // UTMアクセス拠点発
        return this.clonePolicy.policyUtmType1!.accessPointToInternetList.find(
          (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
        )!;
      }
    },
    /** IP Masqueradeポリシーリスト */
    ipMasqueradePolicyList(): Type1IpMasqueradeList {
      return this.clonePolicy.policyIPMasqueradeType1List!.find(
        (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
      )!;
    },
    /** Destination NATポリシーリスト */
    natPolicyList(): Type1NatPolicyList {
      return this.clonePolicy.policyNatType1List!.find(
        (v) => v.zoneRelationId === this.zoneRelationItem.zoneRelationId
      )!;
    },
  },
  methods: {
    tdClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
    /**
     * ポリシーテーブルのtrクラス.
     * 編集不可デフォルトポリシーの場合は特定のクラスを返す
     */
    policyRowClass(
      item: Pick<GetPolicy, "policyCategoryInfo">
    ): string[] | undefined {
      return item.policyCategoryInfo === "UNEDITABLE_DEFAULT_POLICY"
        ? ["gray-out"]
        : undefined;
    },
    /** UTMのルールを編集 */
    async editPolicyUTM() {
      this.utmPolicyList.policyList = await this.$modal.show(
        InternetType1PolicyRuleUtmViewRD,
        {
          zone: this.zoneRelationItem,
          policyList: this.utmPolicyList.policyList,
          type1Policy: this.clonePolicy,
          type1Service: this.type1Service,
        }
      );
    },
    /** IP Masqueradeのルールを編集 */
    async editPolicyIpMasquerade() {
      this.ipMasqueradePolicyList.policyList = await this.$modal.show(
        InternetType1PolicyRuleIpMasqViewRD,
        {
          zone: this.zoneRelationItem,
          policyList: this.ipMasqueradePolicyList.policyList,
          type1Policy: this.clonePolicy,
          type1Service: this.type1Service,
        }
      );
    },
    /** Destination NATのルールを編集 */
    async editPolicyDestinationNAT() {
      this.natPolicyList.policyList = await this.$modal.show(
        InternetType1PolicyRuleDestNatViewRD,
        {
          zone: this.zoneRelationItem,
          policyList: this.natPolicyList.policyList,
          type1Policy: this.clonePolicy,
          type1Service: this.type1Service,
        }
      );
    },
    /** ポリシー編集完了 */
    async submit() {
      (
        this.$refs.modal as typeof VueFinalModal & { ok: (e: unknown) => void }
      ).ok(this.clonePolicy);
    },
  },
});
</script>
<style lang="scss" scoped>
:deep(th) {
  font-size: 90%;
}
</style>
